/**
 * THE GEOMETRY SIDECAR — how long to draw a mark, never what it says.
 *
 * The web never carries a binding's raw value (`ask-answer.spec.ts`: "never
 * surfaces the raw value or valuePath"), yet a bar, a bridge step or a trend
 * point needs a magnitude. So the server — the only place a value is read —
 * hands the canvas one ratio per drawn placeholder: the figure divided by the
 * largest |figure| in the same exhibit, in [-1, 1], sign kept. A ratio is not
 * a business figure; it is never rendered or formatted, only drawn. Every
 * label the reader sees still comes from the binding's governed display
 * strings (#8842 option A).
 *
 * A placeholder with no bound value gets NO entry, so the canvas draws
 * nothing for it rather than a guessed length.
 */
import type { AskHelixNumericBindingDto } from '../../ask-helix';
import type { AskDeclaredExhibitBody } from './declared-exhibit';
import { numericBindingIsEngineBound } from './numeric-binding-engine-bound';

/** Reserved drawing metadata within the existing numeric geometry sidecar. */
export const BRIDGE_AXIS_FLOOR = '__bridge_axis_floor';
export const BRIDGE_MIN_STEP_FRACTION = '__bridge_min_step_fraction';

/** Placeholder → drawing ratio in [-1, 1], plus optional bridge drawing metadata. */
export type AskExhibitGeometry = Readonly<Record<string, number>>;

/** Every drawn placeholder of an exhibit, in slot order. */
export function askExhibitPlaceholders(exhibit: AskDeclaredExhibitBody): readonly string[] {
  switch (exhibit.kind) {
    case 'kpi':
      return [
        ...exhibit.figures.map((figure) => figure.figure),
        ...(exhibit.disclosures ?? []).map((figure) => figure.figure),
        ...(exhibit.zeroDisclosures ?? []).map((figure) => figure.figure),
      ];
    case 'ranked_bars':
      return exhibit.members.map((member) => member.figure);
    case 'trend':
      return [...exhibit.points.map((point) => point.figure), ...(exhibit.change ? [exhibit.change.figure] : [])];
    case 'bridge':
      return [exhibit.from.figure, ...(exhibit.steps ?? []).map((step) => step.figure),
        exhibit.to.figure, exhibit.change.figure];
    case 'decision_table':
      // #8861 slice 3: the disclosed rows are placed figures too.
      return [...exhibit.rows, ...(exhibit.disclosureRows ?? [])].flatMap((row) =>
        row.cells.filter((cell): cell is string => cell !== null));
    case 'scenario_option_set':
      return [
        ...exhibit.options.flatMap((option) => option.figures.map((figure) => figure.figure)),
        ...exhibit.planFigures.map((figure) => figure.figure),
      ];
    case 'status_board':
      return exhibit.items.flatMap((item) =>
        [item.actual, item.target].filter((figure): figure is string => figure !== null));
    default:
      return [];
  }
}

export function askExhibitGeometry(
  exhibit: AskDeclaredExhibitBody,
  bindings: readonly AskHelixNumericBindingDto[],
): AskExhibitGeometry {
  // Only fence-passing figures get a length (review note @ 8161b846).
  const drawable = new Map<string, AskHelixNumericBindingDto>();
  for (const binding of bindings) {
    if (typeof binding.value === 'number' && Number.isFinite(binding.value)
      && numericBindingIsEngineBound(binding)) {
      drawable.set(binding.placeholder, binding);
    }
  }
  const drawn: Array<readonly [string, number]> = [];
  const units = new Set<string>();
  for (const placeholder of askExhibitPlaceholders(exhibit)) {
    const binding = drawable.get(placeholder);
    if (binding === undefined || typeof binding.value !== 'number') continue;
    drawn.push([placeholder, binding.value]);
    units.add(binding.unit);
  }
  // One scale means one unit: mixed units get no geometry at all.
  if (units.size > 1) return {};
  const largest = Math.max(0, ...drawn.map(([, value]) => Math.abs(value)));
  const geometry = Object.fromEntries(drawn.map(([placeholder, value]) =>
    [placeholder, largest === 0 ? 0 : value / largest]));
  return exhibit.kind === 'bridge' && exhibit.steps?.length
    ? bridgeDrawingScale(exhibit, geometry) : geometry;
}

/** Zoom positive running totals; incomplete geometry retains the original zero axis. */
function bridgeDrawingScale(
  exhibit: Extract<AskDeclaredExhibitBody, { kind: 'bridge' }>,
  geometry: AskExhibitGeometry,
): AskExhibitGeometry {
  const placeholders = [exhibit.from.figure, ...exhibit.steps!.map(step => step.figure), exhibit.to.figure];
  if (placeholders.some(key => geometry[key] === undefined)) return geometry;
  let level = geometry[exhibit.from.figure];
  const levels = [level, geometry[exhibit.to.figure]];
  for (const step of exhibit.steps!) {
    level += geometry[step.figure];
    levels.push(level);
  }
  const low = Math.min(...levels);
  const range = Math.max(...levels) - low;
  // Leave room below the lowest connector for the minimum-height mark.
  const floor = low > 0 && range > 0 ? Math.max(0, low - range * 0.2) : 0;
  // Zoom must not amplify normalization/addition roundoff into visible gaps or
  // consume the padding. A conservative error budget covers every running sum;
  // it must fit within one millionth of the retained lower headroom. Otherwise
  // the server explicitly keeps the zero axis and the original drawing ratios.
  const roundoff = Number.EPSILON * levels.length * Math.max(...levels.map(Math.abs));
  const headroom = low - floor;
  const canZoom = floor > 0 && roundoff <= headroom * 1e-6
    && Math.abs(level - geometry[exhibit.to.figure]) <= roundoff
    && headroom >= range * 0.2 - roundoff;
  return { ...geometry, [BRIDGE_AXIS_FLOOR]: canZoom ? floor : 0, [BRIDGE_MIN_STEP_FRACTION]: 0.06 };
}
